import { useQuery } from "@tanstack/react-query";
import { getOutToday } from "@/features/leave/leave.api";

/// Everyone approved-out today, workspace wide. Held by every member
/// (`LEAVE_VIEW_OUT_TODAY`), unlike the calendar and report, which narrow by
/// permission.
///
/// `staleTimeMs` is an option because this now has two callers with different
/// needs: the away panel on the leave page, which the reader arrived at to see
/// exactly this and should get fresh, and the presence boundary, which mounts
/// it for the whole session on every page and wants it quiet.
export function useOutToday(
  workspaceId: string | undefined,
  date?: string,
  options?: { staleTimeMs?: number; refetchOnFocus?: boolean; refetchIntervalMs?: number },
) {
  return useQuery({
    queryKey: ["leave", "out-today", workspaceId, date ?? "today"],
    queryFn: () => {
      if (!workspaceId) throw new Error("workspaceId required");
      return getOutToday(workspaceId, date);
    },
    enabled: Boolean(workspaceId),
    staleTime: options?.staleTimeMs ?? 30_000,
    // The client default is `refetchOnWindowFocus: false`, which is right for
    // a page that mounts, reads and unmounts. It is wrong for a query mounted
    // for the whole session: stale data with nothing observing it afresh is
    // data that never updates. Opt in per caller.
    ...(options?.refetchOnFocus ? { refetchOnWindowFocus: true } : {}),
    ...(options?.refetchIntervalMs ? { refetchInterval: options.refetchIntervalMs } : {}),
  });
}
